// ...existing imports...

import { DataNode } from 'components/WorkspaceEditor/types'
import { useCallback } from 'react'
import { FindNodeResult, findTreeNodeWithPath } from '../utils/TreeNodesUtils'

export interface NodeSearchStrategy {
    search(nodes: DataNode[], targetId: string): FindNodeResult | null
}

// Strategy 1: Direct key match
export class DirectKeySearch implements NodeSearchStrategy {
    search(nodes: DataNode[], targetId: string): FindNodeResult | null {
        return findTreeNodeWithPath(nodes, targetId)
    }
}

// Strategy 4: Content item includes
export class ContentItemIncludesSearch implements NodeSearchStrategy {
    search(nodes: DataNode[], targetId: string): FindNodeResult | null {
        for (const node of nodes) {
            if (node.contentItemNodes && node.contentItemNodes.length > 0) {
                const matchingContentItem = node.contentItemNodes.find(
                    (item) =>
                        item.id?.includes(targetId) ||
                        targetId.includes(item.id || '')
                )
                if (matchingContentItem) {
                    return {
                        nodeKey: node.key,
                        pathToNode: [node.key],
                        isDirectMatch: true,
                    }
                }
            }
            if (node.children && node.children.length > 0) {
                const childResult = this.search(node.children, targetId)
                if (childResult) {
                    return {
                        ...childResult,
                        pathToNode: [node.key, ...childResult.pathToNode],
                    }
                }
            }
        }
        return null
    }
}

// Utility to run strategies in order
export const findTreeNodeWithStrategies = (
    nodes: DataNode[],
    targetId: string,
    strategies: NodeSearchStrategy[]
): FindNodeResult | null => {
    for (const strategy of strategies) {
        const result = strategy.search(nodes, targetId)
        if (result?.isDirectMatch) return result
    }
    return null
}

// Importing the necessary types and utility functions
const strategies: NodeSearchStrategy[] = [
    new DirectKeySearch(),
    new ContentItemIncludesSearch(),
]

/**
 * A function to get tree node information by content ID.
 * This function uses a set of strategies to find the node.
 * @param treeData
 * @param targetId
 * @returns A object that returns the node information or null if not found.
 * */
export const useMemoizedTreeSearch = (treeData: DataNode[]) => {
    return useCallback(
        (targetId: string) => {
            const nodeInfo = findTreeNodeWithStrategies(
                treeData,
                targetId,
                strategies
            )

            return (
                nodeInfo ?? {
                    nodeKey: targetId,
                    pathToNode: [targetId],
                    isDirectMatch: false,
                }
            )
        },
        [strategies, treeData]
    )
}
